import * as B from '@blueprintjs/core';
import classNames from 'classnames';
import * as I from 'immutable';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import LayersIcon from 'app/components/Library/Icons/LayersIcon';
import {NavSidebarItem} from 'app/components/NavSidebar/NavSidebarItem';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization/types';
import {useProjects} from 'app/providers/ProjectsProvider';
import featureFlags, {isLensPlaygroundUser, useUserInfo} from 'app/utils/featureFlags';
import {alphanumericSort} from 'app/utils/featureUtils';
import {getOrgPrefix} from 'app/utils/organizationUtils';
import {makeUuidPrefix} from 'app/utils/routeUtils';
import {shouldTreatAsParentOrg} from 'app/utils/userUtils';

import {useNavSidebarContext} from './NavSidebarProvider';
import cs from './styles.module.scss';

export interface NavItem {
  title: string;
  icon: B.IconName | (() => JSX.Element) | null;
  urlSlug: string;
  active: (currentUrlPath: string, urlSlug: string, isCollapsed: boolean) => boolean;
  /* subItems are not allowed to have subItems for now */
  subItems?: Omit<NavItem, 'subItems'>[];
  disabled?: boolean;
}
const ICON_SIZE = 16;
const getNavItems = (
  profile: I.MapAsRecord<I.ImmutableFields<ApiOrganizationUser>>,
  currentOrganization: I.MapAsRecord<I.ImmutableFields<ApiOrganization>>,
  profileOrganizations: I.List<I.MapAsRecord<I.ImmutableFields<ApiOrganization>>>
): NavItem[] => {
  /* some settings items aren't relevant for parent orgs
   * because they are not allowed to have their own portfolios */
  const treatAsParentOrg = shouldTreatAsParentOrg(
    profile,
    currentOrganization,
    profileOrganizations
  );

  return [
    {
      urlSlug: 'projects',
      title: 'Home',
      icon: 'home',
      active: (currentUrlPath: string, urlSlug: string) => currentUrlPath.startsWith(urlSlug),
    },
    {
      urlSlug: 'projects', // Extra slash hack to make key unique
      title: 'Portfolios',
      icon: 'folder-open',
      // Never show as active since it is the same as home
      active: () => false,
    },
    {
      urlSlug: 'settings/layers',
      title: 'Library',
      icon: () => <LayersIcon size={ICON_SIZE} />,
      disabled: treatAsParentOrg,
      active: (currentUrlPath: string, urlSlug: string) => currentUrlPath.startsWith(urlSlug),
    },
    {
      urlSlug: 'dashboard',
      title: 'Dashboard',
      icon: 'control',
      active: (currentUrlPath: string, urlSlug: string) => currentUrlPath.startsWith(urlSlug),
    },
    ...(featureFlags.AGENT_IN_NAVBAR(currentOrganization, profile)
      ? [
          {
            urlSlug: 'agent',
            title: 'Workflows',
            icon: 'clean',
            active: (currentUrlPath: string, urlSlug: string) => currentUrlPath.startsWith(urlSlug),
          } as NavItem,
        ]
      : []),
    ...(featureFlags.AUTOMATIONS(currentOrganization, profile)
      ? [
          {
            urlSlug: 'automations',
            title: 'Automations',
            icon: 'clean',
            active: (currentUrlPath: string, urlSlug: string) => currentUrlPath.startsWith(urlSlug),
          } as NavItem,
        ]
      : []),
    {
      urlSlug: 'settings',
      title: 'Settings',
      icon: 'cog',
      // Only show as active if the sidebar is collapsed, otherwise we'll show the specific item
      active: (currentUrlPath: string, urlSlug: string, isCollapsed: boolean) =>
        currentUrlPath.startsWith(urlSlug) && isCollapsed && !currentUrlPath.includes('layers'),
      subItems: [
        {
          urlSlug: 'settings/user',
          title: 'Profile',
          icon: 'user',
          disabled: isLensPlaygroundUser(profile),
          active: (currentUrlPath: string, urlSlug: string) => currentUrlPath === urlSlug,
        },
        {
          urlSlug: 'settings/team',
          title: 'Team',
          icon: 'person',
          disabled: isLensPlaygroundUser(profile),
          active: (currentUrlPath: string, urlSlug: string) => currentUrlPath === urlSlug,
        },
        {
          urlSlug: 'settings/organization',
          title: 'Organization',
          icon: 'office',
          disabled: isLensPlaygroundUser(profile),
          active: (currentUrlPath: string, urlSlug: string) => currentUrlPath === urlSlug,
        },
        {
          urlSlug: 'settings/billing',
          title: 'Billing and Contracts',
          icon: 'dollar',
          disabled: isLensPlaygroundUser(profile),
          active: (currentUrlPath: string, urlSlug: string) => currentUrlPath === urlSlug,
        },
        {
          urlSlug: 'settings/notifications',
          title: 'Notifications',
          icon: 'notifications',
          disabled: isLensPlaygroundUser(profile) || treatAsParentOrg,
          active: (currentUrlPath: string, urlSlug: string) => currentUrlPath === urlSlug,
        },
        ...(featureFlags.AGENT_CHAT(currentOrganization, profile)
          ? [
              {
                urlSlug: 'settings/chat',
                title: 'Chat agent',
                icon: 'chat',
                active: (currentUrlPath: string, urlSlug: string) => currentUrlPath === urlSlug,
              } as Omit<NavItem, 'subItems'>,
            ]
          : []),
        {
          urlSlug: 'settings/overlays',
          title: 'Overlay Uploads',
          icon: 'new-layers',
          disabled: isLensPlaygroundUser(profile) || treatAsParentOrg,
          active: (currentUrlPath: string, urlSlug: string) => currentUrlPath === urlSlug,
        },
      ],
    },
  ];
};

export const NavSidebarContainer: React.FunctionComponent<React.PropsWithChildren<{}>> = ({
  children,
}) => (
  <div className={cs.container}>
    <NavSidebar />
    {children}
  </div>
);

const NavSidebar: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [currentOrganization, profile, profileOrganizations] = useUserInfo();
  const {expandedItems, setExpandedItems, collapsedView, setCollapsedView} = useNavSidebarContext();

  const [projectsMap, , , {loading: isProjectsLoading}] = useProjects();
  const sortedProjects = React.useMemo(
    () =>
      (projectsMap || I.OrderedMap())
        .valueSeq()
        .toList()
        .sortBy((project) => project!.get('name'), alphanumericSort)
        .toList(),
    [projectsMap]
  );

  const navItemsWithPortfolios = React.useMemo(() => {
    const navItems = getNavItems(profile, currentOrganization, profileOrganizations);
    const portfolios = sortedProjects
      .map(
        (project) =>
          ({
            title: project!.get('name'),
            icon: null,
            urlSlug: `projects/${makeUuidPrefix(project!.get('id'))}`,
            active: (currentUrlPath: string, urlSlug: string) => currentUrlPath === urlSlug,
          }) as NavItem
      )
      .toArray();
    return navItems.map((item) =>
      item.title === 'Portfolios' ? {...item, subItems: portfolios} : item
    );
  }, [currentOrganization, profile, profileOrganizations, sortedProjects]);

  const handleItemClick = (item: NavItem, navItemPath: string) => {
    if (item.subItems && !collapsedView) {
      const isExpanded = expandedItems.includes(item.urlSlug);
      setExpandedItems((prev) =>
        isExpanded ? prev.filter((id) => id !== item.urlSlug) : [...prev, item.urlSlug]
      );
    } else {
      navigate(navItemPath);
    }
  };

  return (
    <div className={classNames(cs.sidebar, {[cs.collapsed]: collapsedView})}>
      <div className={cs.navContent}>
        <B.Menu>
          {navItemsWithPortfolios.map((item) => {
            const {urlSlug} = item;
            const navItemPath = `/${getOrgPrefix(currentOrganization!)}/${urlSlug}`;
            return (
              <NavSidebarItem
                key={urlSlug + item.title}
                item={item}
                navItemPath={navItemPath}
                collapsedView={collapsedView}
                isProjectsLoading={isProjectsLoading}
                expandedItems={expandedItems}
                handleItemClick={handleItemClick}
              />
            );
          })}
        </B.Menu>
      </div>
      <div className={cs.footer}>
        <B.Menu>
          <B.MenuItem
            icon={<B.Icon icon={collapsedView ? 'double-chevron-right' : 'double-chevron-left'} />}
            text={collapsedView ? undefined : 'Collapse navigation'}
            onClick={() => setCollapsedView(!collapsedView)}
            title={collapsedView ? 'Expand navigation' : undefined}
          />
        </B.Menu>
      </div>
    </div>
  );
};
